/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
export namespace tqconstants {
    export enum RequestMethod {
        GET = "GET",
        POST = "POST",
        DELETE = "DELETE",
        PUT = "PUT",
        PATCH = "PATCH",
        HEAD = "HEAD",
    }

    export enum HeaderKey {
        ACCESS_TOKEN = "X-Access-Token",
        API_KEY = "X-API-Key",
        ANS_APP_ID = "x-adobe-app-id",
        X_REQUEST_ID = "x-request-id",
        AUTHORIZARION = "Authorization",
        USER_AUTHORIZARION = "x-user-token",
        SUBSET_SERVICE_AUTHORIZATION = "x-af-auth-token",
        COLLABORATION_AUTHORIZATION = "x-af-collab-token",
        BYPASS_AUTHORIZARION = "x-bypass-auth",
        CONTENT_TYPE = "Content-Type",
        CONTENT_LEGTH = "Content-Length",
        LOCATION = "Location",
        IF_NONE_MATCH = "If-None-Match",
        IF_MODIFIED_SINCE = "If-Modified-Since",
        IF_MATCH = "If-Match",
        ETAG = "ETag",
        ACCEPT_ENCODING = "Accept-Encoding",
        CONTENT_ENCODING = "Content-Encoding",
        ACCEPT = "Accept",
        RETRY_AFTER = "Retry-After",
        USER_AGENT = "User-Agent",
        SESSION_ID = "Session-id",
    } // namespace header_field

    export enum HeaderValue {
        ACCEPT_JSON = "application/json",
        BEARER = "Bearer ", // note trailing space
        ALL = "*/*",
        MULTIPART_FORM_DATA = "multipart/form-data",
    } // namespace header_field

    export interface APIHeader<Key, Value> {
        key: Key;
        value: Value;
    }

    export type APIResult = "success" | "failure";

    export type APIError = {
        ErrorCode: string;
        Description: string;
    };

    export type APIResponse<Res> = {
        Result: APIResult;
        Response: Res | APIError;
    };

    export enum ServerUrl {
        TYPEKIT_PROD = "https://cctypekit.adobe.io",
        TYPEKIT_STAGE = "https://cctypekit-relstage.adobe.io",
        FASTER_PROD = "https://faster.typekit.net",
        FASTER_STAGE = "https://faster-staging.typekit.net",
        USE_FONTS_PROD = "https://use.typekit.net",
        USE_FONTS_STAGE = "https://use-staging.typekit.net",
        BYOF_PROD = "https://byof.adobe.io",
        BYOF_STAGE = "https://byof-stage.adobe.io",
        ANS_PROD = "https://notify.adobe.io",
        ANS_STAGE = "https://notify-stage.adobe.io",
    }

    export enum ServerAPI {
        TYPEKIT_HEALTHCHECK = "/healthcheck/all",
        TYPEKIT_SEARCH = "/v1/variations/match",
        TYPEKIT_DOWNLOAD = "/v1/variations/",
        TYPEKIT_TAG_CATALOGS = "/v1/tag_catalog",
        TYPEKIT_CATALOG_URL = "/v1/font_catalog",
        TYPEKIT_TOKEN = "/v1/cdn/token",
        // TYPEKIT_TYPESUPPORT = "/v1/type_support",
        TYPEKIT_SELECTIONS = "/v1/selections",
        TYPEKIT_GET_FAMILIES = "/v1/families/",
        TYPEKIT_SEARCH_FAMILIES = "/v1/families",
        TYPEKIT_RECOMMENDATION_URLS = "/v1/recommendations/endpoints",
        USEFONT_USAGE = "/v1/font_uses",
        TYPEKIT_LANG_NAMES = "/v1/filters/languages",
        USEFONT_DOWNLOAD_PREFIX = "/ff/",
        USEFONT_DOWNLOAD_SUFFIX_RAW_PROTECTED = "/p/",
        USEFONT_DOWNLOAD_SUFFIX_RAW = "/j/",
        USEFONT_DOWNLOAD_SUFFIX_WOFF = "/d/",
        USEFONT_DOWNLOAD_SUFFIX_WOFF2 = "/l/",
        BYOF_LIST = "/font_list",
        BYOF_DOWNLOAD = "/download_auth",
        BYOF_UPLOAD_HANDSHAKE = "/upload_url",
        BYOF_UPLOAD_ACCESS_CHECK = "/access_level",
        BYOF_AUTH_DELETE = "/authorization",
        BYOF_BLOCKLISTED_FONTS_CHECK = "/blacklisted_fonts/postscript_names",
        ANS_QUERY_NOTIFICATIONS = "/ans/v1/notifications",
        COLLABORATION_FONT_AUTH_URL = "/v1/collaboration/font_auth",
    }

    export enum ServerQueryParams {
        INSTALL_STATE_KEY = "install_state=",
        INSTALL_STATE_VALUE = "os,cc",

        LIBRARY_KEY = "library=",
        LIBRARY_VALUE_TAGS = "full",

        PAGE_ID_KEY = "page=",
        PER_PAGE_ID_KEY = "per_page=",

        LOCALE_KEY = "locale=",
        CULTURAL_LOCALE_KEY = "cultural_locale=",

        VERSION_KEY = "v=",
        VERSION_VALUE_TAGS = "1.0.0",

        SEARCH_KEY = "q=",

        BROWSE_MODE_KEY = "browse_mode=",
        BROWSE_MODE_VALUE_ALL = "all",

        CHECKSUM_KEY = "checksum=",
        CHECKSUMS_KEY = "checksums=",

        PAGE_SIZE_KEY = "page_size=",
        CONFIG_VERSION_KEY = "config_version=",
        CURRENT_TIMESTAMP_KEY = "from=",
        FUTURE_TIMESTAMP_KEY = "to=",
    }

    export enum ResponseStatus {
        OK = 200,
        NO_CONTENT = 204,
        PARTIAL_CONTENT = 206,

        REDIRECTED = 301,
        FOUND = 302,
        NOT_MODIFIED = 304,
        USE_PROXY = 305,
        REDIRECT_TEMP = 307,
        REDIRECT_PERMANENTLY = 308,

        BAD_REQUEST = 400,
        UNAUTHORIZED = 401,
        FORBIDDEN = 403,
        NOT_FOUND = 404,
        METHOD_NOT_ALLOWED = 405,
        PROXY_REQUIRED = 407,
        REQUEST_TIMEOUT = 408,
        TOO_MANY_REQUESTS = 429,
        RETRY_WITH = 449,

        SERVER_ERROR = 500,
        NOT_IMPLMENTED = 501,
        BAD_GATEWAY = 502,
        SERVICE_UNAVAILABLE = 503,
        GATEWAY_TIMEOUT = 504,
        NETWORK_AUTH_NEEDED = 511,
        NETWORK_READ_TIMEOUT = 598,
        NETWORK_CONNECT_TIMEOUT = 599,
    }

    export const defaultPollInterval = 10;
}
